// Reconnect vs re-meet — the one rule both phones live by (docs/blocks/signup-meetings.md).
//
// A night runs until 4am in the meeting's local time (the moments night key). Connecting
// again with someone you already know:
//   - before that night ends AND within ~1 km of where the meeting started → RECONNECT
//     (the same meeting: nothing new is recorded)
//   - after the night ends, OR more than ~1 km away                        → RE-MEET
//     (a new meeting with its own time, place and moments)
// No position on either side → the time alone decides.
//
// Only the deciding phone (the host — the one whose code was found) runs this; the other
// phone adopts its answer and the meeting key it mints, so the two can never disagree near
// the 4am or ~1 km line.
//
// Permanent data layer: pure, no React, no storage — carries to the native app.

import { nanoid } from 'nanoid';
import { nightKey } from '../moments/nightKey';
import { haversineMiles } from '../nearby/buildNearbyGroups';

export const MEETING_KIND = Object.freeze({
  FIRST: 'first',
  RECONNECT: 'reconnect',
  REMEET: 'remeet',
});

// ~1 km — "clearly a different place"; indoor fixes drift 100–500 m (signup-meetings § The rule)
export const RECONNECT_RADIUS_MILES = 0.62;

export const mintMeetingKey = () => `mtg_${nanoid(12)}`;

const toCoords = (c) =>
  c && Number.isFinite(c.latitude) && Number.isFinite(c.longitude)
    ? { latitude: c.latitude, longitude: c.longitude }
    : null;

/**
 * The meeting a person's record describes, among their rows (newest first): the row with
 * the record's key, or — a record from before keys — their newest key-less row. The one
 * rule every reader and writer uses (venue writes, the late place fill, Detail's latest
 * card, the host's decision), so they always mean the same meeting.
 */
export const currentMeetingRow = (rows, meetingKey) =>
  (meetingKey
    ? (rows || []).find((r) => r.meeting_key === meetingKey)
    : (rows || []).find((r) => !r.meeting_key)) || null;

/** A stored meeting row (meetings table) → the rule's meeting shape. */
export const meetingFromRow = (row) =>
  row
    ? {
        key: row.meeting_key || null,
        metAt: row.met_at || row.met_date || null,
        metUtcOffset: Number.isFinite(row.met_utc_offset) ? row.met_utc_offset : null,
        coords: toCoords(row.location_data?.coordinates),
      }
    : null;

/** The person's own connection record → the rule's meeting shape (when no row exists). */
export const meetingFromMetadata = (metadata) =>
  metadata?.meetingDate
    ? {
        key: metadata.meetingKey || null,
        metAt: metadata.meetingDate,
        metUtcOffset: Number.isFinite(metadata.meetingUtcOffset) ? metadata.meetingUtcOffset : null,
        coords: toCoords(
          metadata.locationData?.coords || metadata.locationData?.venueInfo?.coordinates
        ),
      }
    : null;

/**
 * @param {Object} p
 * @param {Object|null} p.existing  The current meeting ({metAt, metUtcOffset, coords}) or null
 *   for a pair that has never met.
 * @param {string} p.nowIso  The connect instant (ISO).
 * @param {Object|null} p.hostCoords  The deciding phone's position now ({latitude, longitude}).
 * @returns {'first'|'reconnect'|'remeet'}
 */
export const decideMeeting = ({ existing, nowIso, hostCoords }) => {
  if (!existing?.metAt) return MEETING_KIND.FIRST;

  // Both nights read in the meeting's own local frame, so travel or a viewer's clock
  // can't move the 4am line.
  const meetingNight = nightKey(existing.metAt, existing.metUtcOffset);
  const nowNight = nightKey(nowIso, existing.metUtcOffset);
  if (!meetingNight || meetingNight !== nowNight) return MEETING_KIND.REMEET;

  const here = toCoords(hostCoords);
  if (existing.coords && here) {
    const miles = haversineMiles(
      existing.coords.latitude,
      existing.coords.longitude,
      here.latitude,
      here.longitude
    );
    if (miles > RECONNECT_RADIUS_MILES) return MEETING_KIND.REMEET;
  }
  return MEETING_KIND.RECONNECT;
};

/** Whole minutes from the meeting's start to now (the count event; never negative). */
export const minutesSinceMeetingStart = (existing, nowIso) => {
  const start = Date.parse(existing?.metAt || '');
  const now = Date.parse(nowIso || '');
  if (Number.isNaN(start) || Number.isNaN(now)) return null;
  return Math.max(0, Math.round((now - start) / 60000));
};
